import React, { useState, useEffect } from 'react';
import { makeAssistantToolUI } from '@assistant-ui/react';
import { chatService } from '../../services/api';
import { globalChatState } from '../../contexts/ChatStateProvider';

const ModelBuildWidget = ({ args, status, toolCallId, result }) => {
  const [autoMode, setAutoMode] = useState(false);
  const [buildId, setBuildId] = useState(null);

  // Debug logging
  console.log('ModelBuildWidget props:', { args, status, toolCallId, result });

  const modelName = args?.model_name || 'ML Model';
  const statusMessage = status?.statusMessage || 'Initializing build...';
  const needsInput = status?.needsInput || false;

  // Load initial auto_mode state and buildId from session
  useEffect(() => {
    const state = globalChatState.getSnapshot();
    if (state.currentSessionId) {
      const sessionState = globalChatState.getSessionState(state.currentSessionId);
      setAutoMode(sessionState.autoMode);
      setBuildId(sessionState.buildId || result?.build_id || null);
    }
  }, [result]);

  // Subscribe to session state changes
  useEffect(() => {
    const unsubscribe = globalChatState.subscribe((state) => {
      if (state.currentSessionId) {
        const sessionState = globalChatState.getSessionState(state.currentSessionId);
        setAutoMode(sessionState.autoMode);
        setBuildId(sessionState.buildId || result?.build_id || null);
      }
    });
    return unsubscribe;
  }, [result]);

  const handleAutoModeToggle = async (checked) => {
    setAutoMode(checked);

    // Get current session ID
    const state = globalChatState.getSnapshot();
    if (state.currentSessionId) {
      // Call backend to persist auto_mode
      try {
        await chatService.setMode(state.currentSessionId, checked);
        // Update local session state
        globalChatState.updateSessionState(state.currentSessionId, { autoMode: checked });
        console.log('Auto-mode updated:', checked);
      } catch (error) {
        console.error('Failed to update auto-mode:', error);
        // Revert on error
        setAutoMode(!checked);
      }
    }
  };

  return (
    <div className="my-6 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-slate-800">
      {/* Header */}
      <div className="px-6 py-4 bg-gray-50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1">
              Model Build
            </div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
              {modelName}
            </h4>
          </div>

          {/* Auto-mode toggle */}
          <label className="flex items-center gap-2 cursor-pointer group">
            <span className="text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wider">
              Auto mode
            </span>
            <div className="relative">
              <input
                type="checkbox"
                checked={autoMode}
                onChange={(e) => handleAutoModeToggle(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
            </div>
          </label>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {/* Build ID */}
        {buildId && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              Build ID
            </div>
            <p className="text-sm font-mono text-gray-700 dark:text-gray-300">
              {buildId}
            </p>
          </div>
        )}

        {/* Status message */}
        {statusMessage && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              Status
            </div>
            <div className="flex items-start gap-2">
              {status.type === 'running' && (
                <svg className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                {statusMessage}
              </p>
            </div>
          </div>
        )}

        {/* Warning if needs input */}
        {needsInput && !autoMode && (
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="text-sm text-amber-800 dark:text-amber-200">
                Waiting for your response above
              </span>
            </div>
          </div>
        )}

        {/* View Details Link */}
        {buildId && (
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <a
              href={`/models/${buildId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
            >
              View Model Details
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export const ModelBuildToolUI = makeAssistantToolUI({
  toolName: "initiate_model_build",
  render: (props) => <ModelBuildWidget {...props} />
});